import { useState, useEffect } from 'react'
import { supabase } from './supabase'

export default function GolfStats({ session, embedded }) {
  const [stats, setStats] = useState(null)
  const [rounds, setRounds] = useState([])
  const [golfProfile, setGolfProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadStats()
  }, [session])

  const loadStats = async () => {
    setLoading(true)

    // Load golf profile
    const { data: gp } = await supabase
      .from('golf_profiles')
      .select('*')
      .eq('id', session.user.id)
      .single()
    setGolfProfile(gp)

    // Load all closed scorecards with match info
    const { data: sc } = await supabase
      .from('scorecards')
      .select('*, matches(name, course_name, course_par, hole_pars, num_holes, course_rating, slope_rating, tee_time)')
      .eq('player_id', session.user.id)
      .eq('status', 'closed')
      .order('created_at', { ascending: false })

    if (!sc || sc.length === 0) {
      setStats(null)
      setRounds([])
      setLoading(false)
      return
    }

    // Process rounds
    const processed = sc.map(card => {
      const m = card.matches
      const scores = card.hole_scores || []
      const pars = m?.hole_pars || []
      const totalPar = pars.reduce((a, b) => a + b, 0)
      const grossScore = scores.reduce((a, b) => b != null ? a + b : a, 0)
      const holesPlayed = scores.filter(s => s != null).length
      const vsParTotal = grossScore - totalPar

      // Scoring distribution
      const breakdown = { eagle: 0, birdie: 0, par: 0, bogey: 0, double: 0, worse: 0 }
      scores.forEach((s, i) => {
        if (s == null) return
        const vp = s - (pars[i] || 4)
        if (vp <= -2) breakdown.eagle++
        else if (vp === -1) breakdown.birdie++
        else if (vp === 0) breakdown.par++
        else if (vp === 1) breakdown.bogey++
        else if (vp === 2) breakdown.double++
        else breakdown.worse++
      })

      return {
        id: card.id,
        date: m?.tee_time || card.created_at,
        course: m?.course_name || 'Unknown Course',
        matchName: m?.name,
        grossScore,
        vsParTotal,
        totalPar,
        holesPlayed,
        breakdown,
        hasRating: !!(m?.course_rating && m?.slope_rating),
      }
    }).filter(r => r.holesPlayed > 0)

    setRounds(processed)

    if (processed.length === 0) {
      setStats(null)
      setLoading(false)
      return
    }

    // Calculate aggregate stats
    const totalRounds = processed.length
    const avgGross = processed.reduce((a, r) => a + r.grossScore, 0) / totalRounds
    const avgVsPar = processed.reduce((a, r) => a + r.vsParTotal, 0) / totalRounds
    const bestRound = processed.reduce((best, r) => r.vsParTotal < best.vsParTotal ? r : best)
    const worstRound = processed.reduce((worst, r) => r.vsParTotal > worst.vsParTotal ? r : worst)

    // Aggregate scoring breakdown
    const totalBreakdown = { eagle: 0, birdie: 0, par: 0, bogey: 0, double: 0, worse: 0 }
    processed.forEach(r => {
      Object.keys(totalBreakdown).forEach(k => { totalBreakdown[k] += r.breakdown[k] })
    })
    const totalHoles = Object.values(totalBreakdown).reduce((a, b) => a + b, 0)

    setStats({ totalRounds, avgGross, avgVsPar, bestRound, worstRound, totalBreakdown, totalHoles })
    setLoading(false)
  }

  const fmt = (dt) => dt ? new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'
  const fmtVsPar = (v) => v > 0 ? '+' + v.toFixed(1) : v === 0 ? 'E' : v.toFixed(1)
  const vpColor = (v) => v < 0 ? '#00563F' : v > 0 ? '#C0392B' : '#1A1814'

  const hcpColor = golfProfile?.hcp_type === 'ghin' ? '#00563F' : golfProfile?.hcp_type === 'crossbet' ? '#C19A6B' : '#7A7570'
  const hcpLabel = golfProfile?.hcp_type === 'ghin' ? 'GHIN' : golfProfile?.hcp_type === 'crossbet' ? 'CROSSBET' : 'Manual'

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#7A7570', fontFamily: 'DM Sans, sans-serif' }}>Loading stats...</div>
    </div>
  )

  return (
    <div style={s.screen}>
      <div style={embedded ? s.bodyEmbedded : s.body}>



        {!stats ? (
          <div style={s.emptyCard}>
            <div style={s.emptyIcon}>⛳</div>
            <div style={s.emptyText}>No completed rounds yet</div>
            <div style={s.emptySub}>Complete and close a scorecard to see your stats here</div>
          </div>
        ) : (
          <>
            {/* Scoring Average */}
            <div style={s.sectionTitle}>SCORING AVERAGE</div>
            <div style={s.statsRow}>
              <div style={s.statCard}>
                <div style={s.statLabel}>AVG GROSS</div>
                <div style={s.statValue}>{stats.avgGross.toFixed(1)}</div>
              </div>
              <div style={s.statCard}>
                <div style={s.statLabel}>AVG VS PAR</div>
                <div style={{ ...s.statValue, color: vpColor(stats.avgVsPar) }}>{fmtVsPar(stats.avgVsPar)}</div>
              </div>
              <div style={s.statCard}>
                <div style={s.statLabel}>ROUNDS</div>
                <div style={s.statValue}>{stats.totalRounds}</div>
              </div>
            </div>

            {/* Best/Worst */}
            <div style={s.sectionTitle}>RECORDS</div>
            <div style={s.recordsCard}>
              <div style={s.recordRow}>
                <div>
                  <div style={s.recordLabel}>BEST ROUND</div>
                  <div style={s.recordCourse}>{stats.bestRound.course}</div>
                  <div style={s.recordDate}>{fmt(stats.bestRound.date)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ ...s.recordScore, color: vpColor(stats.bestRound.vsParTotal) }}>
                    {fmtVsPar(stats.bestRound.vsParTotal)}
                  </div>
                  <div style={s.recordGross}>{stats.bestRound.grossScore} strokes</div>
                </div>
              </div>
              <div style={{ ...s.recordRow, borderTop: '1px solid #EDE8D8' }}>
                <div>
                  <div style={s.recordLabel}>WORST ROUND</div>
                  <div style={s.recordCourse}>{stats.worstRound.course}</div>
                  <div style={s.recordDate}>{fmt(stats.worstRound.date)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ ...s.recordScore, color: vpColor(stats.worstRound.vsParTotal) }}>
                    {fmtVsPar(stats.worstRound.vsParTotal)}
                  </div>
                  <div style={s.recordGross}>{stats.worstRound.grossScore} strokes</div>
                </div>
              </div>
            </div>

            {/* Scoring Breakdown */}
            <div style={s.sectionTitle}>SCORING BREAKDOWN</div>
            <div style={s.breakdownCard}>
              {[
                { key: 'eagle', label: 'Eagle or better', color: '#C19A6B', icon: '🦅' },
                { key: 'birdie', label: 'Birdie', color: '#00563F', icon: '🐦' },
                { key: 'par', label: 'Par', color: '#1A1814', icon: '⚪' },
                { key: 'bogey', label: 'Bogey', color: '#7A7570', icon: '🔵' },
                { key: 'double', label: 'Double Bogey', color: '#C0392B', icon: '🔴' },
                { key: 'worse', label: 'Triple+', color: '#8B0000', icon: '⛔' },
              ].map(({ key, label, color, icon }) => {
                const count = stats.totalBreakdown[key]
                const pct = stats.totalHoles > 0 ? (count / stats.totalHoles * 100).toFixed(0) : 0
                return (
                  <div key={key} style={s.breakdownRow}>
                    <div style={s.breakdownLeft}>
                      <span style={s.breakdownIcon}>{icon}</span>
                      <span style={s.breakdownLabel}>{label}</span>
                    </div>
                    <div style={s.breakdownRight}>
                      <div style={s.breakdownBar}>
                        <div style={{ ...s.breakdownFill, width: `${pct}%`, background: color }} />
                      </div>
                      <span style={{ ...s.breakdownPct, color }}>{pct}%</span>
                      <span style={s.breakdownCount}>({count})</span>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Last 10 rounds */}
            <div style={s.sectionTitle}>ROUND HISTORY</div>
            <div style={s.historyCard}>
              {rounds.slice(0, 10).map((r, i) => (
                <div key={r.id} style={{ ...s.historyRow, ...(i > 0 ? { borderTop: '1px solid #EDE8D8' } : {}) }}>
                  <div style={s.historyLeft}>
                    <div style={s.historyCourse}>{r.course}</div>
                    <div style={s.historyDate}>{fmt(r.date)}</div>
                  </div>
                  <div style={s.historyRight}>
                    <div style={{ ...s.historyVsPar, color: vpColor(r.vsParTotal) }}>
                      {fmtVsPar(r.vsParTotal)}
                    </div>
                    <div style={s.historyGross}>{r.grossScore}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const s = {
  screen: { display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif", background: '#FDF9E7' },
  body: { padding: '16px 24px 48px', display: 'flex', flexDirection: 'column', gap: 12 },
  bodyEmbedded: { display: 'flex', flexDirection: 'column', gap: 12 },
  hcpCard: { background: '#0A0A0A', borderRadius: 20, padding: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  hcpLeft: {},
  hcpLabel: { fontSize: 11, fontWeight: 700, color: '#444', letterSpacing: 1.5 },
  hcpValue: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 52, color: '#FDF9E7', lineHeight: 1, marginTop: 4 },
  hcpType: { fontSize: 11, fontWeight: 700, marginTop: 4 },
  hcpRight: { textAlign: 'right' },
  hcpRounds: { fontSize: 13, color: '#555', fontWeight: 600 },
  hcpProgress: { fontSize: 11, color: '#444', marginTop: 4 },
  emptyCard: { background: '#FFFFFF', borderRadius: 20, padding: '14px 18px', border: '1px solid #E0DDD4', textAlign: 'center', paddingTop: 28, paddingBottom: 28 },
  emptyIcon: { fontSize: 40, marginBottom: 12 },
  emptyText: { fontSize: 16, fontWeight: 700, color: '#1A1814', marginBottom: 6 },
  emptySub: { fontSize: 13, color: '#7A7570' },
  sectionTitle: { fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 16, color: '#7A7570', letterSpacing: 1.5, marginTop: 4 },
  statsRow: { display: 'flex', gap: 8 },
  statCard: { flex: 1, background: '#FFFFFF', borderRadius: 16, padding: '14px 12px', border: '1px solid #E0DDD4', textAlign: 'center' },
  statLabel: { fontSize: 9, fontWeight: 700, color: '#7A7570', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 6 },
  statValue: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: '#1A1814' },
  recordsCard: { background: '#FFFFFF', borderRadius: 20, padding: 18, border: '1px solid #E0DDD4', display: 'flex', flexDirection: 'column', gap: 0 },
  recordRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' },
  recordLabel: { fontSize: 10, fontWeight: 700, color: '#7A7570', letterSpacing: 1.2 },
  recordCourse: { fontSize: 14, fontWeight: 700, color: '#1A1814', marginTop: 2 },
  recordDate: { fontSize: 11, color: '#7A7570', marginTop: 2 },
  recordScore: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 28 },
  recordGross: { fontSize: 11, color: '#7A7570' },
  breakdownCard: { background: '#FFFFFF', borderRadius: 20, padding: 18, border: '1px solid #E0DDD4', display: 'flex', flexDirection: 'column', gap: 10 },
  breakdownRow: { display: 'flex', alignItems: 'center', gap: 8 },
  breakdownLeft: { display: 'flex', alignItems: 'center', gap: 6, width: 130, flexShrink: 0 },
  breakdownIcon: { fontSize: 14 },
  breakdownLabel: { fontSize: 12, color: '#1A1814', fontWeight: 500 },
  breakdownRight: { display: 'flex', alignItems: 'center', gap: 6, flex: 1 },
  breakdownBar: { flex: 1, height: 6, background: '#EDE8D8', borderRadius: 3, overflow: 'hidden' },
  breakdownFill: { height: '100%', borderRadius: 3, transition: 'width 0.3s' },
  breakdownPct: { fontSize: 12, fontWeight: 700, width: 32, textAlign: 'right' },
  breakdownCount: { fontSize: 11, color: '#7A7570', width: 30 },
  historyCard: { background: '#FFFFFF', borderRadius: 20, padding: 18, border: '1px solid #E0DDD4' },
  historyRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' },
  historyLeft: {},
  historyCourse: { fontSize: 14, fontWeight: 600, color: '#1A1814' },
  historyDate: { fontSize: 11, color: '#7A7570', marginTop: 2 },
  historyRight: { textAlign: 'right' },
  historyVsPar: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 22 },
  historyGross: { fontSize: 11, color: '#7A7570' },
}
